// Landing page structure that does not change with language.
// Copy (names, descriptions, captions) lives in ./i18n/<lang>.ts.
// Adding an Agentic App = add one entry to AGENTIC_APPS (+ its copy under `apps` in every locale
// and an optional SCREENS entry); every count and grid on the page is derived from these arrays.

/** Chat App catalog size. The catalog lives on the Estha Hub, so this is maintained by hand. */
export const CHAT_APPS_COUNT = '50';

export type IconName = 'chart' | 'ppt' | 'pdf' | 'doc' | 'mic' | 'code' | 'translate' | 'plus';

export type AgenticAppKey = 'dataAnalyst' | 'pptMaker' | 'pdfReader' | 'documentProcessing' | 'meetingIntelligence' | 'vibeCoding' | 'translate';

export interface AgenticApp {
  key: AgenticAppKey;
  color: string;
  icon: IconName;
  tier: 'free' | 'premium';
  /** Key into SCREENS for the "See them at work" gallery. */
  screen: ScreenId;
}

// Order follows the app's own Agentic Apps page (frontend/src/config/agenticApps.js).
export const AGENTIC_APPS: AgenticApp[] = [
  { key: 'dataAnalyst', color: '#35B97A', icon: 'chart', tier: 'premium', screen: 'analyst' },
  { key: 'pptMaker', color: '#D8802C', icon: 'ppt', tier: 'premium', screen: 'ppt' },
  { key: 'pdfReader', color: '#F06B6B', icon: 'pdf', tier: 'premium', screen: 'pdf' },
  { key: 'documentProcessing', color: '#8E7CF0', icon: 'doc', tier: 'free', screen: 'doc' },
  { key: 'meetingIntelligence', color: '#C4562A', icon: 'mic', tier: 'premium', screen: 'meeting' },
  { key: 'vibeCoding', color: '#5B5FE8', icon: 'code', tier: 'premium', screen: 'vibe' },
  { key: 'translate', color: '#E87A2A', icon: 'translate', tier: 'free', screen: 'translate' },
];

export type ScreenId = 'chat' | 'build' | 'analyst' | 'ppt' | 'pdf' | 'doc' | 'meeting' | 'vibe' | 'translate' | 'private' | 'org';

export interface ScreenStep {
  /** File in /public/landing (without extension). */
  img: string;
  /** App UI name shown beside the caption (kept in English, as in the screenshot). */
  title: string;
  color: string;
}

export interface Screen {
  title: string;
  /** Step shown first. */
  initial: number;
  steps: ScreenStep[];
}

const ACC = { blue: '#2563EB', meeting: '#C8521C', pdf: '#EF6B6B', analyst: '#34C98A', doc: '#9B7CF4', org: '#1F6F5C', ppt: '#D8802C', vibe: '#5B5FE8', translate: '#E87A2A', signal: '#10B981' };
const s = (img: string, title: string, color: string): ScreenStep => ({ img, title, color });

// Real app screens captured from the frontend with demo data (fictional Harbor Labs / Maple & Finch).
// Step labels and captions per language: t.screens[id][i].
export const SCREENS: Record<ScreenId, Screen> = {
  chat: { title: 'Estha — All Chat Apps', initial: 2, steps: [s('chatapps-A', 'All Chat Apps', ACC.blue), s('chatapps-B', 'All Chat Apps', ACC.blue), s('chatapps-C', "Operator's Compass", ACC.blue)] },
  build: { title: 'Estha — Create App', initial: 1, steps: [s('builder-A', 'Create App', ACC.blue), s('builder-B', 'Quick Bot', ACC.blue), s('builder-C', 'Playground', ACC.blue), s('builder-D', 'Playground', ACC.blue)] },
  analyst: { title: 'Data Analyst', initial: 2, steps: [s('analyst-A', 'Data Analyst', ACC.analyst), s('analyst-B', 'Data Analyst', ACC.analyst), s('analyst-C', 'Data Analyst', ACC.analyst)] },
  ppt: { title: 'PPT Maker', initial: 2, steps: [s('ppt-A', 'Set up', ACC.ppt), s('ppt-B', 'Review outline', ACC.ppt), s('ppt-C', 'PPT Maker', ACC.ppt)] },
  pdf: { title: 'PDF Reader', initial: 2, steps: [s('pdfreader-A', 'PDF Reader', ACC.pdf), s('pdfreader-B', 'PDF Reader', ACC.pdf), s('pdfreader-C', 'PDF Reader', ACC.pdf)] },
  doc: { title: 'Document Processing', initial: 2, steps: [s('document-A', 'Document Processing', ACC.doc), s('document-B', 'Document Processing', ACC.doc), s('document-C', 'Document Processing', ACC.doc)] },
  meeting: { title: 'Meeting Intelligence', initial: 2, steps: [s('meeting-A', 'Recording', ACC.meeting), s('meeting-B', 'Summarize', ACC.meeting), s('meeting-C', 'Meeting Intelligence', ACC.meeting), s('meeting-D', 'Meeting Intelligence', ACC.meeting)] },
  vibe: { title: 'Vibe Coding: Prototype', initial: 2, steps: [s('vibe-A', 'Vibe Coding', ACC.vibe), s('vibe-B', 'Generating', ACC.vibe), s('vibe-C', 'Prototype', ACC.vibe), s('vibe-D', 'Point-to-edit', ACC.vibe)] },
  // Drawn in code (scripts/mockups/translate.html), not captured — replace with real screens when available.
  translate: { title: 'Translate', initial: 0, steps: [s('translate-A', 'Translate', ACC.translate), s('translate-B', 'Documents', ACC.translate), s('translate-C', 'Glossary', ACC.translate)] },
  private: { title: 'Estha — AI Models', initial: 0, steps: [s('org-E', 'On this Mac', ACC.signal), s('org-D', 'Mac Studio Cluster', ACC.org)] },
  org: { title: 'Estha — My Org', initial: 0, steps: [s('org-A', 'Maple & Finch', ACC.org), s('org-C', 'Enterprise', ACC.org)] },
};

export const YOUTUBE_ID = 'VVGnPrxHZbk';
export const CONTACT_EMAIL = 'partnerships@estha.ai';

/* ---------- Trust & resources — fill these in when the material exists ----------
 * Every block below renders only when it has content, so nothing half-finished ships. */

/** "As covered in" row under the stats bar. Shown as text wordmarks; set `logo` (a file in /public/landing) to use an image instead. */
export const PRESS: { name: string; logo?: string; url: string }[] = [
  // Ordered by weight. One link per outlet (TechNode also ran a Q&A: /2026/09/24/estha-eric-lam-local-ai-privacy-sme-deployment-qa/).
  { name: 'TechNode Global', url: 'https://technode.global/2026/09/08/estha-private-ai-workforce-mac-singapore/' },
  { name: 'SecurityBrief', url: 'https://securitybrief.co.uk/story/estha-launches-private-ai-workforce-on-mac-for-business' },
  { name: 'IT Brief Asia', url: 'https://itbrief.asia/story/estha-launches-private-ai-workforce-on-mac-for-business' },
  { name: 'The Info Bytes', url: 'https://theinfobytes.com/estha-for-mac-private-offline-ai-agents' },
  { name: 'Southeast Asia Connect', url: 'https://southeast-asia-connect.onrender.com/articles/singapore-estha-private-on-device-ai-mac-2026.html' },
];

/** 2–3 quotes from real SME customers, shown above pricing. Quote text is shown as written. */
export const TESTIMONIALS: { quote: string; name: string; role: string; industry: string }[] = [];

/** Extra footer links under "Company". A link with an empty url is hidden. */
export const RESOURCE_LINKS: Record<'security' | 'changelog' | 'docs' | 'support', string> = {
  security: '',
  changelog: '',
  docs: '',
  support: '',
};

/** Custom cover for the film (file in /public/landing) and its length, e.g. '1:42'. Empty = YouTube's own thumbnail. */
export const FILM_POSTER = '';
export const FILM_DURATION = '';
